import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  History
} from '@mui/icons-material';
import { formatDate } from '../../utils/formatters';

interface ApprovalItem {
  id: string;
  type: string;
  title: string;
  description: string;
  requestedBy: string;
  requestedDate: Date;
  status: 'pending' | 'approved' | 'rejected';
  priority: 'low' | 'medium' | 'high';
  metadata?: Record<string, any>;
}

interface ApprovalQueueProps {
  items: ApprovalItem[];
  onApprove: (itemId: string, notes?: string) => Promise<void>;
  onReject: (itemId: string, notes?: string) => Promise<void>;
  onView: (item: ApprovalItem) => void;
}

export const ApprovalQueue: React.FC<ApprovalQueueProps> = ({
  items,
  onApprove,
  onReject,
  onView
}) => {
  const [selectedItem, setSelectedItem] = useState<ApprovalItem | null>(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [action, setAction] = useState<'approve' | 'reject' | null>(null);

  const handleAction = async () => {
    if (!selectedItem || !action) return;

    try {
      if (action === 'approve') {
        await onApprove(selectedItem.id, approvalNotes);
      } else {
        await onReject(selectedItem.id, approvalNotes);
      }
      handleClose();
    } catch (error) {
      console.error('Action failed:', error);
    }
  };

  const handleClose = () => {
    setSelectedItem(null);
    setApprovalNotes('');
    setAction(null);
    setDialogOpen(false);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      default:
        return 'info';
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Approval Queue
      </Typography>

      <List>
        {items.map((item) => (
          <ListItem key={item.id}>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  {item.title}
                  <Chip
                    label={item.priority.toUpperCase()}
                    color={getPriorityColor(item.priority)}
                    size="small"
                  />
                </Box>
              }
              secondary={`Requested by ${item.requestedBy} on ${formatDate(item.requestedDate)}`}
            />
            <ListItemSecondaryAction>
              <Tooltip title="View Details">
                <IconButton onClick={() => onView(item)}>
                  <Visibility />
                </IconButton>
              </Tooltip>
              <Tooltip title="Approve">
                <IconButton
                  onClick={() => {
                    setSelectedItem(item);
                    setAction('approve');
                    setDialogOpen(true);
                  }}
                  color="success"
                >
                  <CheckCircle />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton
                  onClick={() => {
                    setSelectedItem(item);
                    setAction('reject');
                    setDialogOpen(true);
                  }}
                  color="error"
                >
                  <Cancel />
                </IconButton>
              </Tooltip>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>

      <Dialog open={dialogOpen} onClose={handleClose}>
        <DialogTitle>
          {action === 'approve' ? 'Approve' : 'Reject'} Request
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={4}
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleAction}
            color={action === 'approve' ? 'success' : 'error'}
            variant="contained"
          >
            {action === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 